import { z } from 'zod';
import { localeSchema } from './common.js';

/**
 * Telegram-only auth (CLAUDE.md §3 `identity-team`): Login Widget on the web,
 * `initData` in a Mini App, one shared signature-verification layer. There are
 * no passwords anywhere in this product.
 */

export const telegramUserSchema = z.object({
  id: z.number().int(),
  first_name: z.string(),
  last_name: z.string().optional(),
  username: z.string().optional(),
  photo_url: z.string().url().optional(),
  language_code: z.string().max(8).optional(),
  is_premium: z.boolean().optional(),
});
export type TelegramUser = z.infer<typeof telegramUserSchema>;

/** Payload posted by the Telegram Login Widget (web admin). */
export const loginWidgetPayloadSchema = z.object({
  id: z.coerce.number().int(),
  first_name: z.string(),
  last_name: z.string().optional(),
  username: z.string().optional(),
  photo_url: z.string().optional(),
  auth_date: z.coerce.number().int(),
  hash: z.string().length(64),
});
export type LoginWidgetPayload = z.infer<typeof loginWidgetPayloadSchema>;

/** Raw `initData` query string handed over by a Mini App. */
export const initDataPayloadSchema = z.object({
  initData: z.string().min(10).max(4096),
});

export const sessionSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    telegramUserId: z.number().int(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    username: z.string().nullable(),
    photoUrl: z.string().nullable(),
    locale: localeSchema,
  }),
  memberships: z.array(
    z.object({
      shopId: z.string().uuid(),
      shopName: z.string(),
      shopSlug: z.string(),
      role: z.enum(['owner', 'staff']),
    }),
  ),
  /** Short-lived bearer token the SPA keeps in memory, not localStorage. */
  token: z.string(),
  expiresAt: z.string().datetime(),
});
export type Session = z.infer<typeof sessionSchema>;

export const inviteStaffSchema = z.object({
  role: z.enum(['owner', 'staff']).default('staff'),
  note: z.string().max(120).optional(),
});

export const acceptInviteSchema = z.object({
  token: z.string().min(10).max(120),
});

export const memberSchema = z.object({
  userId: z.string().uuid(),
  telegramUserId: z.number().int(),
  name: z.string(),
  username: z.string().nullable(),
  photoUrl: z.string().nullable(),
  role: z.enum(['owner', 'staff']),
  status: z.enum(['active', 'removed']),
  joinedAt: z.string().datetime(),
});

/**
 * Storefront callers are customers, not members. They authenticate with
 * `initData` inside the Mini App, or stay anonymous on the plain web and
 * identify themselves with a phone number at checkout (§6.3: never force
 * Telegram before checkout).
 */
export const customerIdentitySchema = z.object({
  telegramUserId: z.number().int().nullable(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
});
